// ==========================================
// FILE: src/services/adminAuthService.ts
// PURPOSE: Admin authentication API service
// ==========================================

import apiClient from './apiClient';
import { useAuthStore, AdminUser } from '../stores/authStore';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  accessToken: string;
  refreshToken: string;
  admin: AdminUser;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

class AdminAuthService {
  /**
   * Login admin user
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>(
        '/admin/auth/login',
        credentials
      );

      const { accessToken, refreshToken, admin } = response.data;

      // Update auth store
      useAuthStore.getState().setAdmin(admin);
      useAuthStore.getState().setTokens(accessToken, refreshToken);

      return response.data;
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to login. Please check your credentials.'
      );
    }
  }

  /**
   * Logout admin user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/admin/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear auth store
      useAuthStore.getState().logout();
    }
  }

  /**
   * Get current admin profile
   */
  async getCurrentAdmin(): Promise<AdminUser> {
    try {
      const response = await apiClient.get<{ success: boolean; admin: AdminUser }>(
        '/admin/auth/me'
      );
      
      const { admin } = response.data;
      
      // Update admin in store
      useAuthStore.getState().setAdmin(admin);
      
      return admin;
    } catch (error: any) {
      console.error('Get current admin error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to get admin profile'
      );
    }
  }

  /**
   * Verify admin token
   */
  async verifyToken(): Promise<boolean> {
    try {
      const response = await apiClient.get<{ success: boolean; admin: AdminUser }>(
        '/admin/auth/verify'
      );
      
      if (response.data.success && response.data.admin) {
        useAuthStore.getState().setAdmin(response.data.admin);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  }

  /**
   * Change admin password
   */
  async changePassword(data: ChangePasswordData): Promise<void> {
    try {
      await apiClient.post('/admin/auth/change-password', data);
    } catch (error: any) {
      console.error('Change password error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to change password'
      );
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<string> {
    try {
      const response = await apiClient.post<{ success: boolean; accessToken: string }>(
        '/admin/auth/refresh'
      );
      
      const { accessToken } = response.data;
      const { refreshToken } = useAuthStore.getState();
      
      if (refreshToken) {
        useAuthStore.getState().setTokens(accessToken, refreshToken);
      }
      
      return accessToken;
    } catch (error: any) {
      console.error('Token refresh error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to refresh token'
      );
    }
  }
}

export default new AdminAuthService();